import { useEffect, useState } from 'react';
import { SCREENS, screenUrl, type Screen } from '@/lib/screens';
import { cn } from '@/components/ui';

/**
 * Pictures of the product, on the website.
 *
 * Every image here is a real capture of the real app — the built frontend
 * driven by a browser against the real backend, taken by
 * `scripts/screenshots.mjs`. Nothing is a mock-up. That is worth the machinery
 * because the alternative rots quietly: a hand-drawn "screenshot" made of divs
 * stops matching the product the first time a button moves, and the website
 * goes on advertising something that no longer exists.
 *
 * The account in them is invented — a small Leeds landlord, six rooms, numbers
 * from the range Ofcom reserves for fiction. No customer's data has ever been
 * on this page.
 *
 * Each screen is captured in both palettes and the one matching the visitor's
 * theme is shown — only that one is fetched, so the second set costs nothing
 * to anybody who never switches.
 */

/**
 * Which palette the page is currently in.
 *
 * Read off the root element rather than from storage, because that attribute
 * is what actually decided the colours on screen — src/main.tsx sets it before
 * first paint, and the toggle in the app changes it live. A visitor who has
 * switched the app to light and then come back to the website would otherwise
 * be shown dark screenshots on a light page, which reads as a stale image
 * rather than a theme.
 *
 * Watched rather than read once, so toggling while this page is open swaps the
 * pictures with everything else.
 */
function usePageTheme(): 'dark' | 'light' {
  const read = () =>
    (document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light');

  const [theme, setTheme] = useState<'dark' | 'light'>(
    () => (typeof document === 'undefined' ? 'light' : read()),
  );

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(read()));
    observer.observe(document.documentElement, {
      attributes: true, attributeFilter: ['data-theme'],
    });
    setTheme(read());
    return () => observer.disconnect();
  }, []);

  return theme;
}

/**
 * The file to show for this screen in this theme.
 *
 * Falls back to the default palette when a screen has no capture in the one
 * asked for — `themes` says what was actually taken, so a run of
 * `npm run screenshots -- --theme=light` leaves the page working rather than
 * pointing at files that were never written.
 */
function srcFor(screen: Screen, theme: 'dark' | 'light') {
  return screenUrl(screen.id, screen.themes.includes(theme) ? theme : 'light');
}

/** A screenshot in a browser-ish frame, so it reads as a picture of software. */
export function ScreenFrame({ screen, priority = false, className }: {
  screen: Screen;
  priority?: boolean;
  className?: string;
}) {
  const theme = usePageTheme();

  if (screen.shape === 'phone') {
    return (
      <div className={cn('mx-auto w-full max-w-[280px]', className)}>
        <div className="rounded-[2rem] border border-line bg-raised p-2 shadow-2xl shadow-black/40">
          <img
            src={srcFor(screen, theme)}
            alt={screen.title}
            width={screen.width}
            height={screen.height}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            className="w-full h-auto rounded-[1.5rem] block"
          />
        </div>
      </div>
    );
  }

  return (
    <figure className={cn('rounded-2xl border border-line bg-raised overflow-hidden shadow-2xl shadow-black/40', className)}>
      {/* Three dots and nothing else. Enough to say "this is an application". */}
      <div className="flex items-center gap-1.5 px-4 h-9 border-b border-line bg-bg/60">
        <span className="h-2.5 w-2.5 rounded-full bg-line" />
        <span className="h-2.5 w-2.5 rounded-full bg-line" />
        <span className="h-2.5 w-2.5 rounded-full bg-line" />
      </div>
      <img
        src={srcFor(screen, theme)}
        alt={screen.title}
        width={screen.width}
        height={screen.height}
        loading={priority ? 'eager' : 'lazy'}
        decoding="async"
        className="w-full h-auto block"
      />
    </figure>
  );
}

/**
 * The tour on the landing page: one big screenshot, switchable.
 *
 * A tab strip rather than a grid of thumbnails. Six screenshots shown at once
 * are six things too small to read, and the point of putting the real app on
 * the page is that somebody can actually read it.
 */
export function ProductTour({ ids }: { ids?: string[] }) {
  const screens = ids
    ? ids.map((id) => SCREENS.find((s) => s.id === id)).filter(Boolean as unknown as (s: Screen | undefined) => s is Screen)
    : SCREENS;

  const [active, setActive] = useState(0);
  const screen = screens[active];

  if (!screen) return null;

  return (
    <div>
      <div className="flex flex-wrap gap-2 justify-center mb-7">
        {screens.map((s, i) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setActive(i)}
            aria-current={i === active}
            className={cn(
              'text-sm px-3.5 h-9 rounded-xl border transition-colors',
              i === active
                ? 'border-brand/40 bg-brand/15 text-ink font-medium'
                : 'border-line text-muted hover:text-ink',
            )}
          >
            {s.title}
          </button>
        ))}
      </div>

      <ScreenFrame screen={screen} priority={active === 0} />

      <p className="text-center text-muted mt-5 max-w-2xl mx-auto leading-relaxed">
        {screen.caption}
      </p>
    </div>
  );
}
